import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, Check, Download } from 'lucide-react';
import { qrFilename } from '../../utils/partnerPortal';

/**
 * Link personal del partner con botón de copiar y QR descargable en PNG (RES-93).
 */
function PartnerLinkCard({ landingUrl, code }) {
  const { t } = useTranslation();
  const qrRef = useRef(null);
  const [copyState, setCopyState] = useState(null); // 'copied' | 'error' | null

  useEffect(() => {
    if (!copyState) return undefined;
    const timer = setTimeout(() => setCopyState(null), 2500);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(landingUrl);
      setCopyState('copied');
    } catch (_err) {
      setCopyState('error');
    }
  };

  const handleDownload = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = qrFilename(code);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="pp-card pp-link-card" aria-labelledby="pp-link-title">
      <div className="pp-link-main">
        <h2 id="pp-link-title" className="pp-card-title">
          {t('partnerPortal.link.title')}
        </h2>
        <p className="pp-muted">{t('partnerPortal.link.subtitle')}</p>
        {landingUrl ? (
          <div className="pp-link-row">
            <input
              className="pp-link-input"
              type="text"
              readOnly
              value={landingUrl}
              aria-label={t('partnerPortal.link.title')}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="btn btn-primary pp-copy-btn"
              onClick={handleCopy}
              data-testid="pp-copy-link"
            >
              {copyState === 'copied' ? (
                <Check size={16} aria-hidden="true" />
              ) : (
                <Copy size={16} aria-hidden="true" />
              )}{' '}
              {copyState === 'copied'
                ? t('partnerPortal.link.copied')
                : t('partnerPortal.link.copy')}
            </button>
          </div>
        ) : (
          <p className="pp-muted">{t('partnerPortal.link.noLink')}</p>
        )}
        {copyState === 'error' && (
          <p className="pp-field-error" role="alert">
            {t('partnerPortal.link.copyError')}
          </p>
        )}
        <p className="pp-code">
          {t('partnerPortal.link.codeLabel')} <strong>{code}</strong>
        </p>
      </div>
      {landingUrl && (
        <div className="pp-qr">
          <div className="pp-qr-canvas" ref={qrRef}>
            <QRCodeCanvas value={landingUrl} size={168} level="M" includeMargin />
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleDownload}
            data-testid="pp-download-qr"
          >
            <Download size={14} aria-hidden="true" /> {t('partnerPortal.link.downloadQr')}
          </button>
        </div>
      )}
    </section>
  );
}

export default PartnerLinkCard;
